import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Check, Info, Play, Video, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { isVenueClaimed, getEmbedUrl } from './venueHelpers';
import VenueQA from '../VenueQA';

// Video Player Card
export const VenueVideoCard = React.memo(({ venue }) => {
  if (!(venue.currentPlan?.toLowerCase() === 'premium' && venue.videoUrl && getEmbedUrl(venue.videoUrl))) return null;
  
  return (
    <div className="bg-white rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden">
      <div className="p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-gradient-to-r from-pink-500 to-red-600 p-3 rounded-2xl shadow-lg">
            <Play className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">Tanıtım Videosu</h3>
            <p className="text-sm text-gray-500">Mekanımızı keşfedin</p>
          </div>
        </div>
        <div className="relative w-full rounded-2xl overflow-hidden shadow-lg" style={{ paddingBottom: '56.25%' }}>
          <iframe
            src={getEmbedUrl(venue.videoUrl)}
            className="absolute top-0 left-0 w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="Mekan Tanıtım Videosu"
            loading="lazy"
          />
        </div>
      </div>
    </div>
  );
});
VenueVideoCard.displayName = 'VenueVideoCard';

// Vertical Video Section
export const VenueVerticalVideo = React.memo(({ venue }) => {
  if (!(venue?.videos && venue.videos.length > 0 && venue.currentPlan === 'Premium')) return null;
  
  return (
    <div className="bg-white rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden mt-6">
      <div className="p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-gradient-to-r from-purple-500 to-pink-600 p-3 rounded-2xl shadow-lg">
            <Video className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Tanıtım Videosu</h2>
            <p className="text-sm text-gray-500">Mekanımızı yakından tanıyın</p>
          </div>
        </div>
        <div className="flex justify-center">
          <div className="relative w-full max-w-[350px] aspect-[9/16] bg-black rounded-2xl overflow-hidden shadow-2xl">
            <video src={venue.videos[0].url} poster={venue.videos[0].thumbnail} controls playsInline className="w-full h-full object-cover" controlsList="nodownload">
              Tarayıcınız video etiketini desteklemiyor.
            </video>
            <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
          </div>
        </div>
      </div>
    </div>
  );
});
VenueVerticalVideo.displayName = 'VenueVerticalVideo';

// Claim Banner
export const VenueClaimBanner = React.memo(({ venue, isBot }) => {
  const navigate = useNavigate();
  if (isBot || isVenueClaimed(venue)) return null;
  
  return (
    <div className="bg-white rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100">
      <div className="bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex-1 text-center sm:text-left">
            <h3 className="text-2xl sm:text-3xl font-extrabold text-white mb-3 drop-shadow-lg">{venue.name} Size mi ait?</h3>
            <p className="text-base sm:text-lg text-white font-semibold drop-shadow-md leading-relaxed">
              Bu sayfa her gün ziyaret alıyor. Bilgilerinizi yönetin, talepleri kaçırmayın, daha fazla müşteriye ulaşın.
            </p>
          </div>
          <div className="flex flex-col items-center sm:items-end">
            <Button
              onClick={() => navigate(`/isortagi-basvuru/${venue.slug}`)}
              size="lg"
              className="bg-white hover:bg-yellow-50 text-pink-600 font-bold text-base sm:text-lg px-8 py-4 sm:py-6 shadow-2xl hover:shadow-pink-500/50 transition-all duration-300 hover:scale-105 w-full sm:w-auto rounded-2xl"
            >
              Sayfanızı Sahiplenin
            </Button>
            <p className="text-white/90 text-xs sm:text-sm mt-2 font-medium">Ücretsiz Doğrulama · 2 dakika sürer</p>
          </div>
        </div>
      </div>
    </div>
  );
});
VenueClaimBanner.displayName = 'VenueClaimBanner';

// About Section
export const VenueAbout = React.memo(({ venue, aboutSectionRef }) => {
  if (isVenueClaimed(venue) && venue.description) {
    return (
      <div ref={aboutSectionRef} className="bg-white rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden">
        <div className="p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-gradient-to-r from-blue-500 to-cyan-600 p-3 rounded-2xl shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Hakkımızda</h2>
              <p className="text-sm text-gray-500">Mekanımız hakkında</p>
            </div>
          </div>
          <p className="text-base text-gray-700 leading-relaxed whitespace-pre-line">{venue.description}</p>
          {venue.googleDescription && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-500 mb-2 flex items-center gap-2">
                <GoogleIcon /> Google'dan
              </p>
              <p className="text-gray-600 italic">"{venue.googleDescription}"</p>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  if ((!isVenueClaimed(venue) || !venue.description) && venue.googleDescription) {
    return (
      <div ref={aboutSectionRef} className="bg-white rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden">
        <div className="p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-gradient-to-r from-pink-500 to-rose-600 p-3 rounded-2xl shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Hakkımızda</h2>
              <p className="text-sm text-gray-500 flex items-center gap-1">
                <GoogleIcon className="w-3 h-3" /> Google'dan alınmıştır
              </p>
            </div>
          </div>
          <p className="text-base text-gray-700 leading-relaxed">{venue.googleDescription}</p>
        </div>
      </div>
    );
  }
  
  return null;
});
VenueAbout.displayName = 'VenueAbout';

const GoogleIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

// Features Card
export const VenueFeatures = React.memo(({ venue }) => {
  if (!venue.features || venue.features.length < 3) return null;
  
  return (
    <div className="bg-white rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden">
      <div className="p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-3 rounded-2xl shadow-lg">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Özellikler</h2>
            <p className="text-sm text-gray-500">{venue.features.length} özellik sunuyoruz</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {venue.features.map((feature, index) => (
            <div key={index} className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-br from-gray-50 to-white border border-gray-100 hover:shadow-md hover:scale-105 transition-all duration-300 group">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <Check className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-800">{feature}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});
VenueFeatures.displayName = 'VenueFeatures';

// Related Locations Card
export const VenueRelatedLocations = React.memo(({ venue }) => {
  return (
    <div className="bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-pink-200">
      <div className="p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-gradient-to-r from-pink-500 to-purple-600 p-3 rounded-2xl shadow-lg">
            <MapPin className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Bölgedeki Mekanlar</h2>
            <p className="text-sm text-gray-600 mt-1">{venue.districtName} ve {venue.cityName} ilindeki davet evleri</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {venue.cityId && (
            <a href={`/davet-evleri?cityId=${venue.cityId}`} className="group bg-white rounded-2xl p-5 border-2 border-transparent hover:border-pink-400 hover:shadow-lg transition-all duration-300 cursor-pointer">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-0.5">Şehirdeki Tüm Mekanlar</p>
                  <p className="text-base font-bold text-gray-900 group-hover:text-pink-600 transition-colors">{venue.cityName} Davet Evleri</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-pink-600 group-hover:translate-x-1 transition-all" />
              </div>
            </a>
          )}
          {venue.districtId && venue.districtName && (
            <a href={`/davet-evleri?cityId=${venue.cityId}&districtId=${venue.districtId}`} className="group bg-white rounded-2xl p-5 border-2 border-transparent hover:border-purple-400 hover:shadow-lg transition-all duration-300 cursor-pointer">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-0.5">İlçedeki Tüm Mekanlar</p>
                  <p className="text-base font-bold text-gray-900 group-hover:text-purple-600 transition-colors">{venue.districtName} Davet Evleri</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-purple-600 group-hover:translate-x-1 transition-all" />
              </div>
            </a>
          )}
        </div>
      </div>
    </div>
  );
});
VenueRelatedLocations.displayName = 'VenueRelatedLocations';

// Q&A Section wrapper
export const VenueQASection = React.memo(({ venue, questions }) => (
  <div className="border-t pt-6 mt-6 order-4 lg:order-3">
    <VenueQA venueId={venue.id} venueName={venue.name} initialQuestions={questions} />
  </div>
));
VenueQASection.displayName = 'VenueQASection';

// Unclaimed Info Note
export const VenueUnclaimedNote = React.memo(({ venue }) => {
  if (isVenueClaimed(venue)) return null;
  return (
    <div className="flex items-start gap-3 p-4 rounded-xl mb-6 bg-amber-50 border border-amber-200">
      <div className="p-2 rounded-lg bg-amber-100">
        <Info className="w-5 h-5 text-amber-600" />
      </div>
      <p className="text-sm leading-relaxed text-amber-700">
        Bilgiler kamusal kaynaklardan otomatik olarak alınmıştır.
      </p>
    </div>
  );
});
VenueUnclaimedNote.displayName = 'VenueUnclaimedNote';
